import { JumpsGame, Ramp } from './JumpsGame.js';
import { JumpsRenderer } from './JumpsRenderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { Position, Direction, positionsEqual } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable, TraceStep, TraceDirResult } from '../../input/KeyboardController.js';

/** One-cell step per direction, for vetting a trace move before the cursor takes it. */
const STEP: Record<Direction, { dr: number; dc: number }> = {
    N: { dr: -1, dc: 0 }, S: { dr: 1, dc: 0 }, E: { dr: 0, dc: 1 }, W: { dr: 0, dc: -1 },
};

export class JumpsInput extends BaseInput<JumpsGame, JumpsRenderer> implements KeyboardControllable {
    private gateMode: boolean = false;

    constructor(
        game: JumpsGame,
        renderer: JumpsRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);

        // Building a ramp: right-click abandons it.
        if (this.game.getCurrentPath().length > 0) {
            this.game.cancelRamp();
            this.onUpdate();
            return;
        }

        const hit = this.detector.detectCell(x, y);
        if (!hit) return;

        const ramp = this.game.getRampAtCell({ row: hit.row, col: hit.col });
        if (ramp) {
            this.game.removeRamp(ramp.id);
            this.onUpdate();
        }
    };

    private handleClick = (e: MouseEvent): void => {
        const { x, y } = this.getCoordinates(e);

        const hit = this.detector.detectCell(x, y);
        if (!hit) return;

        if (this.gateMode && isVortexGame(this.game)) {
            if (this.game.toggleVortexGate(hit)) {
                this.onUpdate();
            }
            return;
        }

        if (this.game.handleClick({ row: hit.row, col: hit.col })) {
            this.onUpdate();
        }
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Ramps are drawn exactly like antennas: hold space and steer to lay the body,
    // release to drop the launcher, press space again and the arrows aim the jump.
    // A ramp lives inside one quarter, so the trace stops at a boundary line.

    placeAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        return this.game.handleClick(pos);
    }

    /** Mirrors the right-click: abandon the build in progress, else delete the ramp
     *  under the cursor. */
    deleteAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        if (this.game.getCurrentPath().length > 0) {
            this.game.cancelRamp();
            return true;
        }
        const ramp = this.game.getRampAtCell(pos);
        if (!ramp) return false;
        return this.game.removeRamp(ramp.id);
    }

    /** Starting on the pending build's own body redraws it from there; starting on a
     *  finished ramp reopens it; anywhere else abandons the build and starts fresh. */
    traceStart(pos: Position): void {
        if (this.gateMode) return;
        this.beginBuild(pos);
    }

    /** Each step draws one more cell of the body — or, stepping back onto the cell
     *  before the end, retracts it. */
    traceStep({ from, to }: TraceStep): boolean {
        if (this.gateMode) return false;
        // Aim mode and blocked presses count as trace steps, so `traceStart` may
        // never have fired: if the build isn't anchored under the cursor, start here.
        if (!this.isBuildingAt(from) && !this.beginBuild(from)) return false;

        const path = this.game.getCurrentPath();
        if (path.length >= 2 && positionsEqual(path[path.length - 2], to)) {
            return this.game.retractBuild();
        }
        return this.game.extendRamp(to);
    }

    /** Releasing drops the launcher, finishing the ramp. */
    traceEnd(): boolean {
        if (this.gateMode) return false;
        return this.game.finishRamp();
    }

    /**
     * Vet a direction press mid-gesture. On a finished launcher the arrows aim the
     * jump; the one pointing back along the body falls through, reopening the ramp as
     * a build, and the rest are inert (a launcher is nothing to trace away from).
     * Otherwise the cursor is pinned to the ramp being drawn: a step onto a digit,
     * another ramp, back over our own body, or across a **quarter boundary** is
     * refused rather than letting the cursor wander off the build.
     */
    traceDirPress(pos: Position, dir: Direction): TraceDirResult {
        if (this.gateMode) return 'blocked';

        const ramp = this.aimTarget(pos);
        if (ramp) {
            if (this.game.getValidJumpDirections(ramp).includes(dir)) {
                // Claim on the direction being *aimable*, not on it having changed —
                // re-pressing the current heading must still hold the cursor here.
                this.game.setJumpDirection(ramp.id, dir);
                return 'handled';
            }
            return dir === JumpsInput.backAlongBody(ramp) ? 'move' : 'blocked';
        }

        const d = STEP[dir];
        return this.canTraceInto(pos, { row: pos.row + d.dr, col: pos.col + d.dc })
            ? 'move' : 'blocked';
    }

    /** Whether a trace step from `from` into `to` may be drawn. */
    private canTraceInto(from: Position, to: Position): boolean {
        // A ramp never crosses a boundary line, so neither does the cursor drawing it.
        if (!this.game.sameQuarter(from, to)) return false;

        const path = this.game.getCurrentPath();
        if (this.isBuildingAt(from)) {
            // Stepping back onto the cell before the end retracts — always allowed.
            if (path.length >= 2 && positionsEqual(path[path.length - 2], to)) return true;
            // Anywhere else on our own body would self-overlap.
            if (path.some(p => positionsEqual(p, to))) return false;
            return this.game.canBuildOn(to);
        }

        // The build is about to (re)start at `from`. If that reopens a finished ramp,
        // its own cells are freed by the reopen, so they don't block.
        const reopened = this.game.getRampAtCell(from);
        if (reopened?.path.some(p => positionsEqual(p, to))) return true;
        return this.game.canBuildOn(to);
    }

    /** Whether the pending build is anchored under the cursor — i.e. this trace is
     *  the one that drew it. */
    private isBuildingAt(pos: Position): boolean {
        const path = this.game.getCurrentPath();
        return path.length > 0 && positionsEqual(path[path.length - 1], pos);
    }

    /** The finished ramp the cursor is aiming: nothing pending, cursor on its launcher. */
    private aimTarget(pos: Position): Ramp | null {
        if (this.game.getCurrentPath().length > 0) return null;
        const ramp = this.game.getRampAtCell(pos);
        if (!ramp) return null;
        return positionsEqual(ramp.path[ramp.path.length - 1], pos) ? ramp : null;
    }

    /** The direction from the launcher back into the ramp's body. */
    private static backAlongBody(ramp: Ramp): Direction | null {
        const path = ramp.path;
        if (path.length < 2) return null;
        const last = path[path.length - 1];
        const prev = path[path.length - 2];
        if (prev.row < last.row) return 'N';
        if (prev.row > last.row) return 'S';
        if (prev.col < last.col) return 'W';
        return 'E';
    }

    /**
     * Begin drawing at `pos`, whatever is already there:
     *  - on the pending build's own body → cut it back to here and carry on
     *  - on a finished ramp → reopen it, keeping its landing side
     *  - anywhere else → abandon any pending build and anchor a fresh landing
     */
    private beginBuild(pos: Position): boolean {
        if (this.game.truncateBuild(pos)) return true;
        this.game.cancelRamp();
        return this.game.getRampAtCell(pos)
            ? this.game.resumeRamp(pos)
            : this.game.startRamp(pos);
    }

    /** The board has hard quarter boundaries; nothing carries over an edge. */
    wrapsCellCursor(): boolean {
        return false;
    }

    /** `X` goes to the first digit — where the cycle has to start. */
    cursorOrigin(): Position | null {
        const grid = this.game.getGrid();
        return grid.getDigitPosition(grid.minDigit);
    }
}
